import React, { useEffect, useState } from "react";
import api from "../../api/client";
import { useLanguage } from "../../contexts/LanguageContext";
import logger from "../../utils/logger.js";
import {
  Download,
  FileText,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import "./MyDownloads.css";

export default function MyDownloads() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    try {
      const res = await api.apiGet("downloads");
      const list = res?.downloads || res?.data || [];
      setDownloads(Array.isArray(list) ? list : []);
    } catch (err) {
      logger.error("Failed to load downloads", err);
      setDownloads([]);
    } finally {
      setLoading(false);
    }
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt).getTime() < Date.now();
  };

  const isLimitReached = (count, max) => {
    const c = Number(count || 0);
    const m = Number(max || 0);
    if (!m) return false;
    return c >= m;
  };

  const getProductName = (item) => {
    if (isAr) {
      return (
        item.product_name_ar ||
        item.product_name ||
        item.product_name_en ||
        "منتج رقمي"
      );
    }

    return (
      item.product_name_en ||
      item.product_name ||
      item.product_name_ar ||
      "Digital Product"
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString(isAr ? "ar" : "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="downloads-container" dir={isAr ? "rtl" : "ltr"}>
        <div className="downloads-loading">
          <div className="downloads-loading-spinner" />
          <p className="downloads-loading-text">
            {isAr ? "جاري التحميل..." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="downloads-container" dir={isAr ? "rtl" : "ltr"}>
      <div className="downloads-header">
        <div className="downloads-header-icon">
          <Download size={20} strokeWidth={2} />
        </div>
        <h2 className="downloads-header-title">
          {isAr ? "تحميلاتي" : "My Downloads"}
        </h2>
      </div>

      {downloads.length === 0 ? (
        <div className="downloads-empty">
          <div className="downloads-empty-icon">
            <FileText size={32} strokeWidth={1.5} />
          </div>
          <p className="downloads-empty-text">
            {isAr ? "لا يوجد منتجات رقمية" : "No downloads yet"}
          </p>
        </div>
      ) : (
        <div className="downloads-grid">
          {downloads.map((item) => {
            const expired = isExpired(item.expires_at);
            const limitReached = isLimitReached(
              item.downloads_count,
              item.max_downloads
            );
            const disabled = expired || limitReached;
            const productName = getProductName(item);
            const downloadCount = Number(item.downloads_count || 0);
            const maxDownloads = Number(item.max_downloads || 0);

            return (
              <div
                key={item.id}
                className={`downloads-card ${disabled ? "disabled" : ""}`}
              >
                <div className="downloads-card-icon">
                  <FileText size={22} strokeWidth={2} />
                </div>

                <h3 className="downloads-card-title">{productName}</h3>

                <div className="downloads-card-info">
                  <div className="downloads-card-meta">
                    <span className="downloads-card-meta-label">
                      {isAr ? "رقم الطلب:" : "Order:"}
                    </span>
                    <span className="downloads-card-meta-value">
                      #{item.order_id}
                    </span>
                  </div>

                  {maxDownloads > 0 && (
                    <div className="downloads-progress-wrapper">
                      <div className="downloads-progress-label">
                        {isAr ? "التحميلات" : "Downloads"}
                      </div>
                      <div className="downloads-progress-bar">
                        <div
                          className="downloads-progress-fill"
                          style={{
                            width: `${Math.min(
                              (downloadCount / maxDownloads) * 100,
                              100
                            )}%`,
                          }}
                        />
                      </div>
                      <div className="downloads-progress-text">
                        {downloadCount} / {maxDownloads}
                      </div>
                    </div>
                  )}

                  {item.expires_at && (
                    <div className="downloads-expiration">
                      <div className="downloads-expiration-label">
                        {isAr ? "ينتهي في:" : "Expires:"}
                      </div>
                      <div className="downloads-expiration-value">
                        {formatDate(item.expires_at)}
                      </div>
                    </div>
                  )}
                </div>

                {expired && (
                  <div className="downloads-status-badge expired">
                    <AlertTriangle size={12} />
                    <span>{isAr ? "انتهت الصلاحية" : "Expired"}</span>
                  </div>
                )}

                {limitReached && !expired && (
                  <div className="downloads-status-badge limit">
                    <AlertCircle size={12} />
                    <span>{isAr ? "بلغ الحد الأقصى" : "Limit reached"}</span>
                  </div>
                )}

                {!disabled && (
                  <div className="downloads-status-badge" style={{ background: "rgba(16, 185, 129, 0.15)", color: "var(--dl-green)", border: "1px solid rgba(16, 185, 129, 0.3)" }}>
                    <CheckCircle size={12} />
                    <span>{isAr ? "جاهز للتحميل" : "Ready"}</span>
                  </div>
                )}

                <a
                  href={disabled ? "#" : item.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (disabled) e.preventDefault();
                  }}
                  className="downloads-button"
                  disabled={disabled}
                >
                  <Download size={16} />
                  <span>{isAr ? "تحميل الآن" : "Download"}</span>
                </a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}